import React from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "../../../button";
import { Image } from "../../../image";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { usePollContext } from "../../../../stores/poll";

import { parseCTAs } from "../parse-ctas";
import { handleCTAClick } from "..";

import { Timer } from "./timer";
import { withCountdown } from "../../../../hocs/with-countdown";

import styles from "../styles.modules.scss";

export type DefaultSlideType = {
    type: "eventEntry" | null;
    id: string;
    background?: ImageSourceSets;
    ctas: InboxCTA[];
    displayTimer: boolean;
    isNew?: boolean;
    timerKind?: InboxEventTimerKind;
    timerLabel: string;
    timerTarget: number;
    title: string;
    countdown: number;
    hideTitle: boolean;
    limit?: number;
    targeting?: Targeting;
    relatedMedia?: RelatedMedia[];
};

const DefaultSlideView = ({
    slide,
    countdown,
}: {
    slide: DefaultSlideType;
    countdown: number | undefined;
}) => {
    const {
        background,
        ctas,
        displayTimer,
        hideTitle,
        timerLabel,
        title,
        limit = 60,
        timerKind,
    } = slide;

    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const limitedTitle = title.substring(0, limit);

    const parsedCTAs = (() => {
        if (!timerKind) {
            return parseCTAs(ctas, handleClick);
        }

        if (timerKind === "SHOW_CTA") {
            return countdown && countdown > 0
                ? []
                : parseCTAs(ctas, handleClick);
        }

        if (timerKind === "HIDE_CTA") {
            return countdown && countdown > 0
                ? parseCTAs(ctas, handleClick)
                : [];
        }

        return parseCTAs(ctas, handleClick);
    })();
    const displayTimerFormatted = Boolean(
        displayTimer && countdown && countdown > 0,
    );

    return (
        <div className={`slide default ${styles.slide}`}>
            <div
                className={`content ${styles.content} ${
                    displayTimerFormatted ? "with-timer" : ""
                }`}
            >
                {background && (
                    <div className={`image ${styles.image}`}>
                        <Image sources={background} cover />
                    </div>
                )}

                {limitedTitle && !hideTitle && (
                    <>
                        <div className={`filter ${styles.filter}`} />

                        <div
                            className={`title ${styles.title}`}
                            data-title={limitedTitle}
                        >
                            {limitedTitle}
                        </div>
                    </>
                )}

                <div className={`wrapper ${styles.wrapper}`}>
                    {displayTimerFormatted && (
                        <div className={`timer ${styles.timer}`}>
                            <Timer
                                format="total-hours"
                                countdown={countdown}
                                timerLabel={timerLabel}
                            />
                        </div>
                    )}

                    {parsedCTAs.length ? (
                        <div
                            className={`ctas ${
                                parsedCTAs.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {parsedCTAs.map(
                                ({ label, onClick, variant }, index) => (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={variant}
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                ),
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const mapTimerProperty = ({ timerTarget }: { timerTarget: number }) =>
    timerTarget;

export const withMappedTimerProperty = withCountdown(mapTimerProperty);

export const DefaultSlide = withMappedTimerProperty(DefaultSlideView);
